import { SDK_VERSION, getApp, getApps, initializeApp } from "firebase/app";
import { env } from "@/shared/constants/env";
import { firebaseConfig, firebaseVapidKey } from "./firebaseConfig";
import { registerDevice, unregisterDevice } from "./services/deviceService";

const SW_PATH = "/sw.js";
const TOKEN_KEY = "alomain.fcm-token";
const REGISTRATION_KEY = "alomain.fcm-registration";
// Réaffirmer l'enregistrement au prochain passage après 24 h, même si le
// token est inchangé. Aucun minuteur ni requête périodique en arrière-plan.
const REGISTRATION_MAX_AGE_MS = 24 * 60 * 60 * 1_000;
type ConfirmedRegistration = { userId: string; token: string; syncedAt: number };
let confirmedRegistration: ConfirmedRegistration | undefined;
let currentToken: string | undefined;
let tokenContext: Awaited<ReturnType<typeof messagingContext>> = null;
// Sérialise activation/rafraîchissement/déconnexion : un enregistrement lent ne
// doit pas remettre le token sur le serveur après sa suppression au logout.
let pending: Promise<unknown> = Promise.resolve();
function serial<T>(action: () => Promise<T>): Promise<T> {
  const result = pending.then(action);
  pending = result.catch(() => undefined);
  return result;
}

export type PushRegistrationResult =
  | { status: "registered" }
  | { status: "denied" }
  | { status: "default" }
  | { status: "unsupported"; reason: string };

export function isWebPushSupported(): boolean {
  return typeof window !== "undefined" && window.isSecureContext &&
    "serviceWorker" in navigator && "PushManager" in window &&
    "Notification" in window && typeof Notification.requestPermission === "function";
}

function rememberToken(token?: string): void {
  currentToken = token;
  try {
    if (token) localStorage.setItem(TOKEN_KEY, token);
    else localStorage.removeItem(TOKEN_KEY);
  } catch { /* Le token reste disponible en mémoire si le stockage est bloqué. */ }
}

function rememberedToken(): string | undefined {
  try { return currentToken ?? localStorage.getItem(TOKEN_KEY) ?? undefined; }
  catch { return currentToken; }
}

function rememberRegistration(value?: ConfirmedRegistration): void {
  confirmedRegistration = value;
  try {
    if (value) localStorage.setItem(REGISTRATION_KEY, JSON.stringify(value));
    else localStorage.removeItem(REGISTRATION_KEY);
  } catch { /* Déduplication en mémoire si le stockage est bloqué. */ }
}

function readRegistration(): ConfirmedRegistration | undefined {
  let stored: string | null;
  try { stored = localStorage.getItem(REGISTRATION_KEY); }
  catch { return confirmedRegistration; }
  // Lire le stockage à chaque fois prend aussi en compte un logout ou un
  // changement de compte effectué dans un autre onglet.
  if (!stored) return undefined;
  try {
    const value: unknown = JSON.parse(stored);
    if (value && typeof value === "object" &&
      "userId" in value && typeof value.userId === "string" &&
      "token" in value && typeof value.token === "string" &&
      "syncedAt" in value && typeof value.syncedAt === "number" && Number.isFinite(value.syncedAt)) {
      return { userId: value.userId, token: value.token, syncedAt: value.syncedAt };
    }
  } catch { /* Un cache invalide entraîne une nouvelle synchronisation. */ }
  return undefined;
}

async function messagingContext() {
  const sdk = await import("firebase/messaging");
  if (!(await sdk.isSupported())) return null;
  const app = getApps().length ? getApp() : initializeApp(firebaseConfig);
  return { sdk, messaging: sdk.getMessaging(app) };
}

/** Même configuration et même version du SDK dans la page et le worker.
 * La query ne contient que des identifiants Firebase publics. La portée reste /.
 */
export async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) return null;
  try {
    const params = new URLSearchParams({ config: JSON.stringify(firebaseConfig), sdk: SDK_VERSION });
    return await navigator.serviceWorker.register(`${SW_PATH}?${params}`, { scope: "/" });
  } catch (error) {
    if (env.isDev) console.warn("[push] enregistrement du Service Worker échoué", error);
    return null;
  }
}

async function activeRegistration(): Promise<ServiceWorkerRegistration> {
  const registration = await registerServiceWorker();
  if (!registration) throw new Error("Le service de notifications est indisponible.");
  const worker = registration.installing ?? registration.waiting ?? registration.active;
  if (!worker) throw new Error("Le service de notifications est indisponible.");
  if (worker.state !== "activated") {
    await new Promise<void>((resolve, reject) => {
      const finish = (error?: Error) => {
        clearTimeout(timer);
        worker.removeEventListener("statechange", check);
        if (error) reject(error); else resolve();
      };
      const check = () => {
        if (worker.state === "activated") finish();
        else if (worker.state === "redundant") finish(new Error("Activation des notifications échouée."));
      };
      const timer = setTimeout(() => finish(new Error("Activation des notifications trop longue.")), 15000);
      worker.addEventListener("statechange", check);
      check();
    });
  }
  return registration;
}

async function registerFcmToken(userId: string): Promise<PushRegistrationResult> {
  const context = await messagingContext();
  if (!context) return { status: "unsupported", reason: "Ce navigateur ne permet pas les notifications push." };
  const registration = await activeRegistration();
  const previousToken = rememberedToken();
  const subscription = await registration.pushManager.getSubscription();
  if (subscription) {
    // Migration des anciens abonnements W3C envoyés comme JSON à /devices.
    if (!previousToken) {
      await unregisterDevice({ token: JSON.stringify(subscription.toJSON()) }).catch(() => undefined);
    }
    const key = subscription.options.applicationServerKey;
    if (key) {
      const existingKey = btoa(String.fromCharCode(...new Uint8Array(key)))
        .replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
      // FCM réutilise l’abonnement du navigateur : une ancienne clé VAPID doit
      // être retirée avant getToken, sinon les envois Firebase échoueront.
      if (existingKey !== firebaseVapidKey.replace(/=+$/, "")) await subscription.unsubscribe();
    }
  }
  tokenContext = context;
  const token = await context.sdk.getToken(context.messaging, {
    vapidKey: firebaseVapidKey,
    serviceWorkerRegistration: registration,
  });
  if (!token) throw new Error("Impossible d’activer les notifications. Réessayez.");
  rememberToken(token);
  const confirmed = readRegistration();
  const age = confirmed ? Date.now() - confirmed.syncedAt : -1;
  if (confirmed?.userId !== userId || confirmed.token !== token ||
    age < 0 || age >= REGISTRATION_MAX_AGE_MS) {
    // Ne mémoriser que les succès : un POST échoué doit pouvoir être retenté
    // au prochain retour en ligne/onglet, même avec le même token Firebase.
    await registerDevice({ userId, token, platform: "web" });
    rememberRegistration({ userId, token, syncedAt: Date.now() });
  }
  if (previousToken && previousToken !== token) {
    await unregisterDevice({ token: previousToken }).catch(() => undefined);
  }
  return { status: "registered" };
}

/** Demande native immédiate ; les navigateurs exigeant un geste sont repris par le hook. */
export async function registerWebPush(userId: string): Promise<PushRegistrationResult> {
  if (!isWebPushSupported()) return {
    status: "unsupported",
    reason: "Ce navigateur ne gère pas les notifications web. Sur iPhone, ajoutez Alomain à l’écran d’accueil.",
  };
  if (Notification.permission === "denied") return { status: "denied" };
  const permission: Promise<NotificationPermission> = Notification.permission === "granted"
    ? Promise.resolve("granted") : Notification.requestPermission();
  return serial(async () => {
    const decision = await permission;
    if (decision !== "granted") return { status: decision };
    return registerFcmToken(userId);
  });
}

/** Renouvelle le token FCM sans provoquer de demande de permission. */
export async function refreshDeviceRegistration(userId: string): Promise<void> {
  if (!isWebPushSupported() || Notification.permission !== "granted") return;
  try { await serial(() => registerFcmToken(userId)); }
  catch (error) {
    if (env.isDev) console.warn("[push] ré-enregistrement de l’appareil échoué", error);
  }
}

/** Affiche également les messages reçus pendant que la page est visible. */
export async function listenForForegroundMessages(): Promise<() => void> {
  if (!isWebPushSupported()) return () => undefined;
  const context = await messagingContext();
  if (!context) return () => undefined;
  return context.sdk.onMessage(context.messaging, (payload) => {
    void navigator.serviceWorker.getRegistration(SW_PATH).then((registration) => {
      registration?.active?.postMessage({ type: "FCM_FOREGROUND", payload });
    }).catch(() => undefined);
  });
}

/** Supprime le token exact, sans en générer un nouveau pendant la déconnexion. */
export async function unregisterWebPush(accessToken?: string): Promise<void> {
  return serial(async () => {
    rememberRegistration();
    const token = rememberedToken();
    if (token) await unregisterDevice({ token, authToken: accessToken }).catch(() => undefined);
    try {
      if (isWebPushSupported()) {
        // deleteToken suppose que getToken a déjà attaché notre worker au SDK.
        // Après un rechargement sans getToken, désabonner directement suffit à
        // invalider l’endpoint ; le SDK remplacera son cache au prochain login.
        if (tokenContext) {
          await tokenContext.sdk.deleteToken(tokenContext.messaging).catch(() => undefined);
        }
        const registration = await navigator.serviceWorker.getRegistration(SW_PATH);
        const subscription = await registration?.pushManager.getSubscription();
        await subscription?.unsubscribe();
      }
    } catch (error) {
      if (env.isDev) console.warn("[push] désabonnement échoué", error);
    } finally { rememberToken(); tokenContext = null; }
  });
}
